import { useEffect, useMemo, useState } from "react";
import { api, IMG } from "@/lib/site";
import ArticleCard from "@/components/site/ArticleCard";
import Reveal from "@/components/site/Reveal";
import Seo from "@/components/site/Seo";
import { PAGE_SEO, breadcrumbLd, articlesItemListLd } from "@/lib/seo";

const filters = ["All", "Foundational", "Environmental", "Scientific", "Emotional", "Guides"];

export default function Articles() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("All");

  useEffect(() => {
    api
      .get("/articles")
      .then((res) => setArticles(res.data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    if (activeFilter === "All") return articles;
    return articles.filter((a) => a.category === activeFilter);
  }, [articles, activeFilter]);

  const featured = visible.find((a) => !a.is_stub) || visible[0];
  const rest = visible.filter((a) => a !== featured);

  return (
    <div data-testid="articles-page">
      <Seo
        {...PAGE_SEO.articles}
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Learning Centre", path: "/articles" },
          ]),
          articlesItemListLd(articles),
        ]}
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={IMG.heroClouds} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#F9F9F6]/85" />
        </div>
        <div className="container-editorial pt-20 pb-12">
          <div className="overline mb-5">Learning Centre</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-forest-900 max-w-4xl">
            Reading on water cremation, aqua cremation, and sustainable pet aftercare.
          </h1>
          <p className="mt-6 max-w-2xl text-lg sm:text-xl text-forest-700 leading-[1.65]">
            Long form, evergreen, and educational. Designed to be useful at your
            own pace.
          </p>
        </div>
      </section>

      <section className="container-editorial">
        <div className="flex flex-wrap gap-2 sm:gap-3 border-b border-sand-300 pb-8" data-testid="articles-filters">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              data-testid={`filter-${f.toLowerCase()}`}
              className={`px-5 py-2 rounded-full text-sm tracking-wide transition-all duration-200 ${
                activeFilter === f
                  ? "bg-forest-900 text-sand-50"
                  : "bg-transparent text-forest-700 hover:bg-sand-100 border border-sand-300"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      <section className="container-editorial section-padding">
        {loading && <p className="text-forest-700">Loading articles…</p>}

        {!loading && featured && (
          <div className="mb-20" data-testid="articles-featured">
            <ArticleCard article={featured} variant="feature" />
          </div>
        )}

        {!loading && rest.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {rest.map((a, i) => (
              <Reveal key={a.slug} delay={Math.min(i * 0.06, 0.45)}>
                <ArticleCard article={a} />
              </Reveal>
            ))}
          </div>
        )}

        {!loading && visible.length === 0 && (
          <p className="text-forest-700">No articles in this category yet.</p>
        )}
      </section>
    </div>
  );
}
